import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../Icon';
import usePageMeta from '../usePageMeta';
import useJsonLd from '../useJsonLd';
import { contactLink } from '../topics';
import { useContact, useSiteContent } from '../SiteContent';
import { activeSite } from '../sites';
import ContactSection from '../components/ContactSection';
import T from '../components/EditableText';
import PageHero from '../components/PageHero';
import StepList from '../components/StepList';
import FaqSection from '../components/FaqSection';
import FactBox from '../components/FactBox';

// Die Motive sind 700×420, die Karten zeigen sie im flachen Streifen — ohne
// eigenen Bildausschnitt schneidet der Center-Crop die Köpfe ab.
// Überschrift und Text jeder Karte stehen als `hauswirtschaft.leistung-N.…`
// in `content/hauswirtschaft.ts`.
const SERVICES = [
  {
    number: 1,
    image: '/website/img/hw-reinigung.jpg',
    position: 'center top',
    alt: 'Haushaltshilfe beim Staubsaugen',
  },
  {
    number: 2,
    image: '/website/img/hw-waesche.jpg',
    position: 'center 40%',
    alt: 'Betreuerin bringt Blumen ins Wohnzimmer',
  },
  {
    number: 3,
    image: '/website/img/hw-betreuung.jpg',
    position: 'center top',
    alt: 'Zwei Frauen sitzen gemeinsam auf dem Sofa',
  },
  {
    number: 4,
    image: '/website/img/hw-einkauf.jpg',
    position: 'center 12%',
    alt: 'Zwei Frauen unterwegs beim Einkaufen',
  },
];

/** Erzeugt die Schlüssel `<präfix>.punkt-1` … `.punkt-<anzahl>`. */
const points = (prefix: string, count: number): string[] =>
  Array.from({ length: count }, (unused, index) => `${prefix}.punkt-${index + 1}`);

/**
 * Die Fragen speisen den sichtbaren Abschnitt und das FAQ-Markup — eine Quelle,
 * denn strukturierte Daten ohne sichtbaren Inhalt verstoßen gegen Googles Richtlinien.
 */
const FAQ_NUMBERS = [1, 2, 3, 4, 5];

const Hauswirtschaft: React.FC = () => {
  const { plain } = useSiteContent();
  const contact = useContact();

  // „Haushaltshilfe" ist das Wort der Suchenden, „Hauswirtschaft" das der
  // Branche — der Titel braucht beide.
  usePageMeta(
    'Haushaltshilfe & stundenweise Betreuung in Gifhorn',
    'Reinigung, Wäsche, Einkäufe und Alltagsbetreuung für Senioren in Gifhorn: ab 37 € pro Stunde, abrechenbar über den Entlastungsbetrag von 131 € im Monat.',
    { standaloneTitle: true, image: '/website/img/hw-hero.jpg', preloadImage: '/website/img/hw-hero.webp' },
  );

  /**
   * `Service` für die Haushaltshilfe mit dem Stundenpreis der Kostenseite, dazu
   * FAQ- und Breadcrumb-Markup — dieselbe Bauart wie auf der 24h-Seite. Der
   * Betrieb selbst (`LocalBusiness`) steht in `public/index.html`.
   */
  const jsonLd = React.useMemo(() => {
    const base = activeSite().url;
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Service',
          '@id': `${base}/hauswirtschaft#service`,
          name: 'Haushaltshilfe und stundenweise Betreuung für Senioren',
          serviceType: 'Haushaltshilfe für Senioren',
          description: plain('hauswirtschaft.einleitung.absatz-2'),
          provider: { '@id': `${base}/#organisation` },
          areaServed: [
            { '@type': 'City', name: 'Gifhorn' },
            { '@type': 'AdministrativeArea', name: 'Landkreis Gifhorn' },
          ],
          url: `${base}/hauswirtschaft`,
          offers: {
            '@type': 'Offer',
            priceCurrency: 'EUR',
            price: '37',
            priceSpecification: { '@type': 'UnitPriceSpecification', price: '37', priceCurrency: 'EUR', unitText: 'Stunde' },
            url: `${base}/kosten`,
          },
        },
        {
          '@type': 'FAQPage',
          mainEntity: FAQ_NUMBERS.map((number) => ({
            '@type': 'Question',
            name: plain(`hauswirtschaft.faq.${number}.frage`),
            acceptedAnswer: { '@type': 'Answer', text: plain(`hauswirtschaft.faq.${number}.antwort`) },
          })),
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Startseite', item: `${base}/` },
            { '@type': 'ListItem', position: 2, name: 'Hauswirtschaft', item: `${base}/hauswirtschaft` },
          ],
        },
      ],
    };
  }, [plain]);

  useJsonLd('hauswirtschaft', jsonLd);

  return (
    <>
      <PageHero
        eyebrowKey="hauswirtschaft.hero.eyebrow"
        titleKey="hauswirtschaft.hero.titel"
        image={{ src: '/website/img/hw-hero.jpg', alt: 'Hauswirtschaftliche Leistungen zu Hause', width: 1400, height: 560, position: '40% center' }}
      >
        <p className="hx-hero__lead">
          <T k="hauswirtschaft.einleitung.absatz-1" />
        </p>
        <div className="btn-row hx-hero__actions">
          <Link to={contactLink('HOME_CARE')} className="btn btn--primary">
            <T k="hauswirtschaft.hinweis.cta" />
          </Link>
          <a href={contact.phoneHref} className="btn btn--on-dark" data-stelle="hero">
            <Icon name="phone" size={18} />
            {contact.phoneLabel}
          </a>
        </div>
      </PageHero>

      {/* Die Antwort in sechs Zeilen, bevor die Seite ins Detail geht. */}
      <section className="shell hx-facts-wrap">
        <FactBox prefix="hauswirtschaft.fakten" />
      </section>

      {/* Einordnung links, Anerkennung nach § 45a rechts */}
      <section className="shell section hx-intro">
        <div className="prose">
          <p>
            <T k="hauswirtschaft.einleitung.absatz-2" />
          </p>
          <p>
            <T k="hauswirtschaft.einleitung.absatz-3" />
          </p>
        </div>
        <aside className="hx-note">
          <span className="hx-note__mark">
            <Icon name="badge-check" size={18} />
            <T k="hauswirtschaft.hinweis.marke" />
          </span>
          <h2 className="h-card">
            <T k="hauswirtschaft.hinweis.titel" />
          </h2>
          <p>
            <T k="hauswirtschaft.hinweis.text" />
          </p>
        </aside>
      </section>

      {/* Leistungen als vier Bildkarten */}
      <section className="shell section--tight">
        <div className="hx-cardgrid hx-cardgrid--4">
          {SERVICES.map((service) => (
            <article key={service.number} className="hx-card">
              <img
                src={service.image}
                alt={service.alt}
                width={700}
                height={420}
                style={{ objectPosition: service.position }}
                loading="lazy"
              />
              <h2 className="h-card">
                <T k={`hauswirtschaft.leistung-${service.number}.titel`} />
              </h2>
              <p>
                <T k={`hauswirtschaft.leistung-${service.number}.text`} />
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* Ablauf */}
      <section className="section--tinted">
        <div className="shell section hx-steps">
          <div className="hx-steps__head">
            <h2 className="h-section">
              <T k="hauswirtschaft.ablauf.titel" />
            </h2>
            <p>
              <T k="hauswirtschaft.ablauf.einleitung" />
            </p>
          </div>
          <StepList keys={points('hauswirtschaft.ablauf', 5)} />
        </div>
      </section>

      {/* Finanzierung — die drei Töpfe der Pflegekasse, Beträge Stand 2026
          (siehe Kommentar in content/hauswirtschaft.ts). */}
      <section className="shell section">
        <div className="hx-band">
          <div className="hx-band__lead">
            <h2 className="h-section">
              <T k="hauswirtschaft.finanzierung.titel" />
            </h2>
            <p>
              <T k="hauswirtschaft.finanzierung.einleitung" />
            </p>
            <p className="hx-band__note">
              <T k="hauswirtschaft.finanzierung.hinweis" />
            </p>
          </div>
          <ul className="hx-band__list">
            {points('hauswirtschaft.finanzierung', 3).map((key) => (
              <li key={key}>
                <T k={key} />
              </li>
            ))}
          </ul>
        </div>
      </section>

      <FaqSection titleKey="hauswirtschaft.faq.titel" numbers={FAQ_NUMBERS} keyOf={(n) => `hauswirtschaft.faq.${n}`} />

      <ContactSection topic="HOME_CARE" />
    </>
  );
};

export default Hauswirtschaft;
